import {useEffect} from 'react';
import {useDispatch, useStore} from 'react-redux';
import {AppDispatch, RootState} from 'store';
import {refreshCustomers} from 'store/customers/customersSlice';
import {refreshCustomerProducts} from 'store/customersProducts/customersProductsSlice';
import {refreshSuppliers} from 'store/suppliers/suppliersSlice';
import {EMPTY_RETRY_MS, RefreshStatus, shouldRefresh} from './masterDataRefreshSchedule';
import {masterDataRefreshLocked} from './masterDataEditLock';

// Dedicated refresh thunks: the regular get* thunks set the async state to 'loading',
// and pages swap their whole content for <Loader/> on it.
const RESOURCES: Array<{ isEmpty: (state: RootState) => boolean; run: (dispatch: AppDispatch) => Promise<unknown> }> = [
    {isEmpty: (s) => s.customersProducts.ids.length === 0, run: (d) => d(refreshCustomerProducts()).unwrap()},
    {isEmpty: (s) => s.customers.ids.length === 0, run: (d) => d(refreshCustomers()).unwrap()},
    {isEmpty: (s) => s.suppliers.ids.length === 0, run: (d) => d(refreshSuppliers()).unwrap()},
];

const useMasterDataRefresh = () => {
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();

    useEffect(() => {
        const startedAt = Date.now();
        const statuses: RefreshStatus[] = RESOURCES.map(() => ({inFlight: false, lastAttemptAt: startedAt, lastAttemptOk: false}));
        const settle = (status: RefreshStatus, ok: boolean) => {
            status.lastAttemptOk = ok;
            status.lastAttemptAt = Date.now();
            status.inFlight = false;
        };
        const tick = () => {
            if (masterDataRefreshLocked()) return;
            const state = store.getState();
            const now = Date.now();
            RESOURCES.forEach((resource, i) => {
                const status = statuses[i];
                if (!shouldRefresh(status, resource.isEmpty(state), now, document.hidden)) return;
                status.inFlight = true;
                resource.run(dispatch).then(() => settle(status, true), () => settle(status, false));
            });
        };
        const id = window.setInterval(tick, EMPTY_RETRY_MS);
        return () => window.clearInterval(id);
    }, [dispatch, store]);
};

export default useMasterDataRefresh;
